import { useMemo, useState } from 'react';
import { getText } from 'i18n/intl';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import {
  selectActivityCreateTemplates,
  selectGroupCreationMode,
} from 'slices/objectGenerationTemplate.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  EActivityDraftGroupConflictModel,
  type TActivityDraftGroupConfig,
} from '@timeedit/types/lib/types/activityGroup.type';
import { Alert, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { conflictModelsAlreadyImplemented } from '..';
import { ArrayOfRules } from '../components/ArrayOfRules';

export interface GeneralInfoProps {
  value: TActivityDraftGroupConfig;
  onChangeValue: (key: string, value: unknown) => void;
}

const GeneralSettings = ({ value, onChangeValue }: GeneralInfoProps) => {
  const objectTypes = useAppSelector(selectObjectTypesList);
  const activityCreateTemplates = useAppSelector(selectActivityCreateTemplates);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);
  const creationMode = useAppSelector(selectGroupCreationMode);

  const [activityCreateTemplateIdChanged, setActivityCreateTemplateIdChanged] =
    useState(false);

  const conflictModelsOptions = Object.keys(EActivityDraftGroupConflictModel)
    .filter((key) =>
      conflictModelsAlreadyImplemented.includes(
        key as EActivityDraftGroupConflictModel
      )
    )
    .map((key) => ({
      value: key,
      label: getText(
        `CONFLICT_MODEL_${
          EActivityDraftGroupConflictModel[
            key as keyof typeof EActivityDraftGroupConflictModel
          ]
        }`
      ),
    }));

  const optionActivityTemplates = useMemo(() => {
    return activityCreateTemplates.map((template) => ({
      value: template._id,
      label: template.name,
    }));
  }, [activityCreateTemplates]);
  const objectTypesOptions = useMemo(() => {
    return objectTypes.map((obj) => ({
      value: obj.id,
      label: obj.name,
    }));
  }, [objectTypes]);
  return (
    <TEDrawerSection label={getText('system_general_setting_title')}>
      <div className={'te-mt-2'}>
        <TEInput.Input
          label={getText('name')}
          value={value.name}
          onChange={(e) => onChangeValue('name', e)}
        />
      </div>
      <div className="te-mt-2">
        <TEInput.LabelWrapper label={getText('objects_type')}>
          <TEInput.Select
            options={objectTypesOptions}
            value={value.objectType}
            onChange={(e) => onChangeValue('objectType', e)}
            style={{ width: '100%' }}
          />
          <br />
          {value.objectType && (
            <a
              href={`/${customerSignature}/configuration/object-types?item1=${value.objectType}`}
              target="_blank"
              rel="noreferrer"
            >
              {getText('activity_group_to_obj_type_def')}
            </a>
          )}
        </TEInput.LabelWrapper>
      </div>
      <div className="te-mt-2">
        <TEInput.Select
          label={getText('conflict_model')}
          value={value.conflictModel}
          options={conflictModelsOptions}
          onChange={(e) => onChangeValue('conflictModel', e)}
        />
      </div>
      <div className="te-mt-2">
        <TEInput.Select
          label={getText('activity_template')}
          tooltip={getText('import_template_expl')}
          value={value.activityCreateTemplateId}
          options={optionActivityTemplates}
          allowClear={false}
          onChange={(e) => {
            onChangeValue('activityCreateTemplateId', e);
            setActivityCreateTemplateIdChanged(true);
          }}
        />
        {(creationMode || activityCreateTemplateIdChanged) && (
          <Alert
            title={getText('group_config_act_change_warn')}
            type="warning"
          />
        )}
      </div>

      <div className="te-mt-2">
        <TEInput.LabelWrapper
          label={getText('ext_id')}
          tooltip={getText('ext_id_expl')}
        >
          <ArrayOfRules
            currentTemplate={value}
            currentArrayOfRules={value.extIdFillWith}
            arrayOfRulesChanged={(changedValue) =>
              onChangeValue('extIdFillWith', changedValue)
            }
          ></ArrayOfRules>
        </TEInput.LabelWrapper>
      </div>
    </TEDrawerSection>
  );
};

export default GeneralSettings;
